// Billing rate types for the partner pricing tab.
//
// The list and its COURIER/WAREHOUSE grouping belong to storage-api's RateType
// enum, and since 2026-09-28 they are served by GET /admin/reference/rate-types —
// the same arrangement GET /admin/reference/vehicle-classes has had all along,
// whose own comment in FleetAdminRoutes says it exists "so frontends don't
// hard-code the list". This tab did hard-code it, under a comment saying it had
// to match the enum, with nothing able to check that it did.
//
// Neither direction of drift was cosmetic. A key listed here that the enum does
// not have means `updatePartnerPricing` refuses the entire request — fees and
// every other rate with it — so one stale string would have blocked all pricing
// edits for that partner. A key the enum has and this list does not is one the
// admin can never add, because the "+ Add rate type" buttons are built from it.

export type RateScope = 'COURIER' | 'WAREHOUSE';

export interface RateTypeRef {
  key: string;
  scope: RateScope;
}

/**
 * Offline fallback, not a source of truth.
 *
 * Used only when the reference call fails. Rates are partner commercial terms and
 * an admin who has opened this tab is usually here to change one, so losing the
 * network should not leave them looking at an empty form with no explanation —
 * but anything here can be out of date, which is the whole reason the endpoint
 * exists. The values are storage-api's as of 2026-09-28.
 */
export const FALLBACK_RATE_TYPES: RateTypeRef[] = [
  { key: 'DOORSTEP_COLLECTION', scope: 'COURIER' },
  { key: 'TIMESLOT_COLLECTION', scope: 'COURIER' },
  { key: 'DOORSTEP_RETURN', scope: 'COURIER' },
  { key: 'TIMESLOT_RETURN', scope: 'COURIER' },
  { key: 'MIN_CHARGE', scope: 'COURIER' },
  { key: 'FAILED_DELIVERY_FEE', scope: 'COURIER' },
  { key: 'STORAGE_PER_BOX_MONTH', scope: 'WAREHOUSE' },
  { key: 'INBOUND_PROCESSING', scope: 'WAREHOUSE' },
  { key: 'OUTBOUND_PICK', scope: 'WAREHOUSE' },
  { key: 'WH_FAILED_DELIVERY_FEE', scope: 'WAREHOUSE' },
];

/** The tab's own two-state toggle, lower-cased because it is also the URL segment. */
export type TabScope = 'courier' | 'warehouse';

/**
 * The rate types an admin may add on this scope's tab.
 *
 * `served` is whatever the reference endpoint returned, or null if the call has
 * not finished or has failed. An empty answer for a scope falls back rather than
 * showing nothing: the API asserts on its side that both scopes are populated, so
 * an empty one here means the call went wrong, not that this deployment has no
 * warehouse rates — and "no rate types at all" is indistinguishable on screen
 * from "this partner is fully configured".
 */
export function rateTypeKeysForScope(scope: TabScope, served: RateTypeRef[] | null): string[] {
  const wanted: RateScope = scope === 'courier' ? 'COURIER' : 'WAREHOUSE';
  const fromApi = (served || []).filter((r) => r.scope === wanted).map((r) => r.key);
  if (fromApi.length > 0) return fromApi;
  return FALLBACK_RATE_TYPES.filter((r) => r.scope === wanted).map((r) => r.key);
}

/**
 * Reads the reference payload defensively. This is an admin surface and the shape
 * comes off the wire, so a malformed entry drops out rather than reaching the
 * rendering as `undefined` and blanking a button.
 */
export function parseRateTypes(payload: unknown): RateTypeRef[] {
  const list = (payload as { rateTypes?: unknown })?.rateTypes;
  if (!Array.isArray(list)) return [];
  return list
    .map((entry) => (entry || {}) as { key?: unknown; scope?: unknown })
    .filter(
      (entry): entry is { key: string; scope: RateScope } =>
        typeof entry.key === 'string' &&
        entry.key.length > 0 &&
        (entry.scope === 'COURIER' || entry.scope === 'WAREHOUSE'),
    )
    .map((entry) => ({ key: entry.key, scope: entry.scope }));
}

// Customer-facing labels stay here rather than on the API: they are UK English
// admin copy (Gate 6), and a key arriving without one is humanised below instead
// of being hidden, so a rate type added to the backend is usable the same day.
export const RATE_TYPE_LABELS: Record<string, string> = {
  DOORSTEP_COLLECTION: 'Doorstep collection',
  TIMESLOT_COLLECTION: 'Timeslot collection',
  DOORSTEP_RETURN: 'Doorstep return',
  TIMESLOT_RETURN: 'Timeslot return',
  MIN_CHARGE: 'Minimum charge',
  FAILED_DELIVERY_FEE: 'Failed delivery fee',
  STORAGE_PER_BOX_MONTH: 'Storage per box / month',
  INBOUND_PROCESSING: 'Inbound processing',
  OUTBOUND_PICK: 'Outbound pick',
  WH_FAILED_DELIVERY_FEE: 'Warehouse failed delivery fee',
};

export function displayRateType(key: string): string {
  if (RATE_TYPE_LABELS[key]) return RATE_TYPE_LABELS[key];
  return key
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
